var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Cartesian2, SceneTransforms, ScreenSpaceEventType, } from "cesium";
import { ConstructionState } from "../../site/trench/earthbrain-trench";
import { AddControlPointAtIndexCommand, AddControlPointOnRoadCommand, RemovePointCommand, UpdateHorizontalAlignmentTypeCommand, UpdatePointPositionCommand, UpdateSlopeBreaksInLongitudinalView, } from "./road-commands";
import { CESIUM_PICKABLE_ENTITY } from "../../types";
import { PositionReference, checkPositionWrtToTileset, } from "../../utilities/local-utilities";
import { Activity } from "../../utilities/activity-logger";
import { CesiumPointSelectorType, } from "../../cesium-utils/point-selector/point-selector";
import { getMarkerDistanceById } from "../../site/alignment/section-marker-resolver";
import { SectionSelectionController } from "../alignment/section-selection-controller";
import { hasMovedPastClickThreshold } from "../alignment/control-point-click";
function isEditable() {
    const retVal = (target, key, descriptor) => {
        const original = descriptor.value;
        descriptor.value = function (...args) {
            const roadEditHandler = this;
            if (roadEditHandler.roadUnderEdit) {
                return original.apply(this, args);
            }
            else {
                console.error(`Call start on RoadEditHandler before calling ${String(key)}`);
            }
        };
        return descriptor;
    };
    return retVal;
}
export class RoadEditHandler {
    _callback;
    _roadUnderEdit;
    _viewer;
    _preview;
    _commandManager;
    _handler;
    _cesiumPicker;
    _cesiumPointSelector_priorityBased;
    _rayCastPointSelector;
    _site;
    _cesiumViewer;
    _sectionSelection;
    _controlPointDragStartPosition;
    _hasShownControlPointDragPreview = false;
    _mosueMoveDebounce = undefined;
    /** true の間は getPointOnMousePos が進行中 */
    _isControlPointMoveInProgress = false;
    /** MOUSE_MOVE で受け取った最新イベント。処理完了後にこの値で次の処理を開始する */
    _pendingControlPointMoveEvent = undefined;
    /** LEFT_UP 時に true にし、進行中の処理結果を破棄する */
    _controlPointMoveCancelled = false;
    /** fire-and-forget で実行中の UpdatePointPositionCommand の Promise */
    _pendingCommandPromise = undefined;
    _isRoadExtension;
    get sectionPlaneDistanceFromStart() {
        return this._sectionSelection?.state.distFromStartPoint ?? 0;
    }
    get roadUnderEdit() {
        return this._roadUnderEdit;
    }
    get cesiumViewer() {
        return this._cesiumViewer;
    }
    get isRoadExtension() {
        return this._isRoadExtension;
    }
    constructor(args, cesiumViewer) {
        this._viewer = args.viewer;
        this._preview = args.preview;
        this._commandManager = args.commandManger;
        this._handler = args.handler;
        this._cesiumPicker = args.cesiumPicker;
        this._site = args.site;
        this._callback = {};
        this._cesiumViewer = cesiumViewer;
        this._isRoadExtension = false;
        this._cesiumPointSelector_priorityBased =
            args.cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.PriorityBased);
        this._rayCastPointSelector =
            args.cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.RayCastBased);
    }
    start(road, callback) {
        this._callback = callback;
        this._roadUnderEdit = road;
        this._roadUnderEdit.constructionState = ConstructionState.UNDER_EDIT;
        this._commandManager.clear();
        this._isRoadExtension = false;
        road.fitToRoad();
        this._sectionSelection = new SectionSelectionController({
            viewer: this._viewer,
            site: this._site,
            host: road,
            tracksUnnamedPviByRatio: true,
            activity: Activity.ROAD_SECTION_PLANE_UPDATED,
            onChange: (distance3d, label) => this._callback.onProgress?.(road.id, distance3d, label),
        });
        this._callback.onStart();
    }
    end() {
        this._commandManager.clear();
        this._sectionSelection?.dispose();
        this._sectionSelection = undefined;
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._preview.setSphereVisibility(false);
        this._preview.setLineVisibility(false);
        this._preview.setVerticalLineEntityVisibility(false);
        this._preview.setLinePosition([]);
        this._roadUnderEdit.constructionState = ConstructionState.READY;
        this._roadUnderEdit = undefined;
        this._isRoadExtension = false;
    }
    cancel() {
        //To Do All changes should be undone
        this.end();
    }
    removeControlPoint(controlPointId) {
        const isControlPointRemovable = this._roadUnderEdit.isControlPointRemovable(controlPointId);
        //Restricting user from deleting last 2 points of the road while editing because we need atleast 2 points to create road.
        // Also restricting the removal of first & last control point
        if (this._roadUnderEdit.controlPointLength > 2 && isControlPointRemovable) {
            this._commandManager.action(new RemovePointCommand(this._roadUnderEdit, controlPointId, this._getCallbackForCommands()), this._cesiumViewer);
        }
    }
    updateHorizontalAlignmentType(horizontalAlignmentType) {
        this._commandManager.action(new UpdateHorizontalAlignmentTypeCommand(this._roadUnderEdit, horizontalAlignmentType, this._getCallbackForCommands()), this._cesiumViewer);
    }
    /**
     * This Function will let you add a new control point in middle from the control point on which this functon is called and the previous control point
     * @param roadID unique ID of the road
     */
    addControlPointOnRoad(controlPointId) {
        this._commandManager.action(new AddControlPointOnRoadCommand(this._roadUnderEdit, controlPointId, this._getCallbackForCommands()), this._cesiumViewer);
        this._sectionSelection?.refresh();
    }
    /**
     * This Function will let you add a new control point on the given point on the center line
     * @param roadID unique ID of the road
     * @param localPoint point on center line of the road in local space
     * @param callback RoadEditCallback
     */
    addControlPointOnCenterLine(localPoint) {
        const closestPoint = this._roadUnderEdit.getClosestPointInfoOnPath(localPoint);
        if (closestPoint.ipIndex !== -1) {
            this._commandManager.action(new AddControlPointAtIndexCommand(this._roadUnderEdit, closestPoint.ipIndex + 1, closestPoint.point, this._getCallbackForCommands()), this._cesiumViewer);
            this._sectionSelection?.refresh();
        }
    }
    /** Selects the section identified by `anchor`, which also decides how it is tracked across edits. */
    selectSection(anchor) {
        this._sectionSelection?.select(anchor);
    }
    /**
     * Selects the section for a longitudinal-view click, deciding how it is tracked across edits:
     * stations by distance, curve markers / control points by id, unnamed PVIs by ratio.
     */
    selectSectionHit(hit) {
        this._sectionSelection?.selectHit(hit);
    }
    refreshSectionSelection() {
        this._sectionSelection?.refresh();
    }
    updateCtrlPointCordiOnRoad(controlPointID, point) {
        if (point) {
            this._site.convertPointsToLocalSpace([point]).then((arr) => {
                const localPos = arr[0];
                const windowCoordinates = SceneTransforms.worldToWindowCoordinates(this._viewer.scene, point);
                const pickedInfo = this._cesiumPicker.getPickablePickedInfo(windowCoordinates);
                if (pickedInfo?.structureInfo?.id === this.roadUnderEdit.id) {
                    pickedInfo.structureInfo = undefined;
                }
                this._commandManager.action(new UpdatePointPositionCommand(this._roadUnderEdit, { position: point, info: pickedInfo, localPosition: localPos }, controlPointID, this._getCallbackForCommands()), this._cesiumViewer);
                this._sectionSelection?.refresh();
            });
        }
    }
    async updateSlopeBreaksFromLongitudinalView(slopeBreaks) {
        if (slopeBreaks && slopeBreaks.length > 0) {
            await this._commandManager.action(new UpdateSlopeBreaksInLongitudinalView(this._roadUnderEdit, slopeBreaks, this._getCallbackForCommands()), this._cesiumViewer);
            return true;
        }
        else {
            this.raiseEventInvalidCtrlPtForRoad(this._roadUnderEdit.id);
            return false;
        }
    }
    /**
     * This function will return Road ID and Road point ID
     * @param x screen coordinate
     * @param y screen coordinate
     */
    async pickPathandPoint(x, y) {
        const result = {
            pathId: undefined,
            entityId: undefined,
            type: undefined,
            entity: undefined,
            position: undefined,
        };
        const posToPick = new Cartesian2(x, y);
        const pickedEntity = this._cesiumPicker.getPickablePickedInfo(posToPick);
        if (pickedEntity) {
            result.pathId = pickedEntity.owner?.id;
            result.entityId = pickedEntity.entity?.id;
            result.type = pickedEntity.pickableType;
            result.entity = pickedEntity.entity;
            result.position = undefined;
            // Use raycast against road geometry for accurate position
            const clickedPoint = await this._rayCastPointSelector.getPointOnMousePos(posToPick, this._roadUnderEdit);
            if (clickedPoint) {
                const [localPosition] = await this._site.convertPointsToLocalSpace([
                    clickedPoint,
                ]);
                result.position = [localPosition.x, localPosition.y, localPosition.z];
            }
        }
        return result;
    }
    //Private methods
    _getCallbackForCommands() {
        return async () => {
            this._sectionSelection?.refresh();
        };
    }
    raiseEventInvalidCtrlPtForRoad(roadId) {
        const event = new CustomEvent("invalidControlPointForRoad", {
            detail: { roadId, type: 0 },
        });
        window.dispatchEvent(event);
    }
    async _controlPointPosUpdate(controlPointID, clickEvent) {
        // Cancel in-progress move processing
        this._controlPointMoveCancelled = true;
        this._pendingControlPointMoveEvent = undefined;
        const isClick = !hasMovedPastClickThreshold(this._controlPointDragStartPosition, clickEvent.position);
        if (isClick) {
            if (getMarkerDistanceById(this._roadUnderEdit.alignment, controlPointID) !==
                undefined) {
                this.selectSection({ type: "point", pointId: controlPointID });
            }
        }
        else {
            const cartesian = await this._cesiumPointSelector_priorityBased.getPointOnMousePos(clickEvent.position);
            if (cartesian) {
                this._site.convertPointsToLocalSpace([cartesian]).then((arr) => {
                    const localPos = arr[0];
                    const positionRef = checkPositionWrtToTileset(localPos, this._roadUnderEdit.associatedTerrain);
                    if (positionRef !== PositionReference.OUTSIDE) {
                        const pickedInfo = this._cesiumPicker.getPickablePickedInfo(clickEvent.position);
                        if (pickedInfo?.structureInfo?.id === this._roadUnderEdit.id) {
                            pickedInfo.structureInfo = undefined;
                        }
                        this._viewer.scene.screenSpaceCameraController.enableRotate = true;
                        const commandPromise = this._commandManager.action(new UpdatePointPositionCommand(this._roadUnderEdit, {
                            position: cartesian,
                            info: pickedInfo,
                            localPosition: localPos,
                        }, controlPointID, this._getCallbackForCommands()), this._cesiumViewer);
                        this._pendingCommandPromise = commandPromise;
                        commandPromise.finally(() => {
                            if (this._pendingCommandPromise === commandPromise) {
                                this._pendingCommandPromise = undefined;
                            }
                        });
                        this._sectionSelection?.refresh();
                    }
                    else {
                        this.raiseEventInvalidCtrlPtForRoad(this._roadUnderEdit.id);
                    }
                });
            }
        }
        this._controlPointDragStartPosition = undefined;
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        // Now need to enable the camera
        this._viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._preview.setSphereVisibility(false);
        // Reset for next drag
        this._controlPointMoveCancelled = false;
        this._isControlPointMoveInProgress = false;
    }
    async _controlPointMove(event) {
        const endPosition = event.endPosition;
        const pos = await this._cesiumPointSelector_priorityBased.getPointOnMousePos(endPosition);
        if (this._controlPointMoveCancelled || !this._controlPointDragStartPosition)
            return;
        if (pos) {
            if (!this._hasShownControlPointDragPreview &&
                hasMovedPastClickThreshold(this._controlPointDragStartPosition, endPosition)) {
                this._preview.setSphereVisibility(true);
                this._preview.createVerticalLineEntityOnSphere();
                this._hasShownControlPointDragPreview = true;
            }
            this._preview.setSpherePosition(pos);
        }
    }
    _throttledControlPointMove(event) {
        this._pendingControlPointMoveEvent = event;
        if (this._isControlPointMoveInProgress) {
            return;
        }
        this._processNextControlPointMove();
    }
    async _processNextControlPointMove() {
        this._isControlPointMoveInProgress = true;
        while (this._pendingControlPointMoveEvent !== undefined) {
            if (this._controlPointMoveCancelled) {
                break;
            }
            const event = this._pendingControlPointMoveEvent;
            this._pendingControlPointMoveEvent = undefined;
            await this._controlPointMove(event);
        }
        this._isControlPointMoveInProgress = false;
    }
    async pickControlPoint(click) {
        // Wait for any pending command to finish to avoid Worker WebSocket queue contention
        if (this._pendingCommandPromise) {
            await this._pendingCommandPromise;
        }
        const pickedEntity = this._cesiumPicker.getAllowPickedEntity(click.position);
        if (pickedEntity) {
            // Finding which path has been clicked
            const selectedPathID = this._site
                .getRoads()
                .find((p) => p.pickEntity(pickedEntity).entity !== undefined);
            if (selectedPathID && pickedEntity.name === "sphereEntity") {
                if (this._roadUnderEdit.id === selectedPathID.id) {
                    // Storing the ID of the control point
                    const controlPointID = pickedEntity.id;
                    this._controlPointDragStartPosition = Cartesian2.clone(click.position);
                    this._hasShownControlPointDragPreview = false;
                    this._handler.setInputAction(this._controlPointPosUpdate.bind(this, controlPointID), ScreenSpaceEventType.LEFT_UP);
                    this._handler.setInputAction(this._throttledControlPointMove.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
                    // Now need to disable the camera
                    this._viewer.scene.screenSpaceCameraController.enableRotate = false;
                }
            }
            else if (pickedEntity.pickInfo?.pickableType ===
                CESIUM_PICKABLE_ENTITY.ALIGNMENT_POINT) {
                this._sectionSelection?.selectPickedPoint(pickedEntity.id);
            }
        }
    }
}
__decorate([
    isEditable()
], RoadEditHandler.prototype, "end", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "cancel", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "removeControlPoint", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "updateHorizontalAlignmentType", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "addControlPointOnRoad", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "addControlPointOnCenterLine", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "selectSection", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "refreshSectionSelection", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "updateCtrlPointCordiOnRoad", null);
__decorate([
    isEditable()
], RoadEditHandler.prototype, "updateSlopeBreaksFromLongitudinalView", null);
//# sourceMappingURL=road-edit-handler.js.map